import { useState } from 'react';
import {
  ArrowLeft,
  MessageCircle,
  MapPin,
  Check,
  Heart,
  Share2,
  Clock,
} from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import {
  getServiceById,
  getVendorById,
  getReviewsForService,
  getCategoryById,
} from '../data/fixtures';
import {
  Button,
  PageSection,
  RatingLine,
  VerifiedBadge,
  Tag,
  Avatar,
} from '../shell/ui';

export function ServiceDetailScreen() {
  const { route, navigate, back } = useRouter();
  const service = getServiceById(route.params?.serviceId || '');
  const vendor = service ? getVendorById(service.vendorId) : null;
  const category = service ? getCategoryById(service.categoryId) : null;
  const reviews = service ? getReviewsForService(service.id) : [];
  const [activePkg, setActivePkg] = useState<string>(
    service?.packages[1]?.id ?? service?.packages[0]?.id ?? '',
  );

  if (!service || !vendor) {
    return (
      <PageSection>
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <h1>Service not found</h1>
          <Button onClick={() => navigate('browse')}>Browse categories</Button>
        </div>
      </PageSection>
    );
  }

  const pkg = service.packages.find((p) => p.id === activePkg) ?? service.packages[0];

  return (
    <>
      {/* Breadcrumb + back */}
      <div
        style={{
          maxWidth: '1240px',
          margin: '0 auto',
          padding: '1.5rem 1.5rem 0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          fontSize: '0.8125rem',
          color: 'var(--color-text-muted)',
        }}
      >
        <button
          onClick={back}
          style={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            color: 'var(--color-text-secondary)',
            marginRight: '0.5rem',
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <span>·</span>
        <button
          onClick={() => navigate('browse')}
          style={{ all: 'unset', cursor: 'pointer' }}
        >
          Browse
        </button>
        <span>›</span>
        <button
          onClick={() => navigate('category', { categoryId: service.categoryId })}
          style={{ all: 'unset', cursor: 'pointer' }}
        >
          {category?.name}
        </button>
        <span>›</span>
        <span style={{ color: 'var(--color-text-primary)' }}>{service.title}</span>
      </div>

      {/* Editorial header */}
      <PageSection paddingY="2.5rem">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: '2rem',
            alignItems: 'start',
          }}
          className="svc-hero"
        >
          {/* Left: title + gallery */}
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 400,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                margin: 0,
                marginBottom: '1rem',
              }}
            >
              {service.title}
            </h1>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap',
                marginBottom: '1.5rem',
              }}
            >
              <button
                onClick={() => navigate('vendor', { vendorId: vendor.id })}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                }}
              >
                <Avatar src={vendor.avatar} size={32} />
                <span style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                  {vendor.name}
                </span>
              </button>
              <RatingLine rating={service.rating} reviewCount={service.reviewCount} />
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-muted)',
                }}
              >
                <MapPin size={13} /> {vendor.location}
              </span>
            </div>

            {/* Gallery */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: '0.75rem',
                height: '460px',
                marginBottom: '2rem',
              }}
              className="svc-gallery"
            >
              <div
                style={{
                  gridRow: '1 / span 2',
                  borderRadius: 'var(--radius-lg)',
                  background: `url(${service.heroImage}) center/cover no-repeat`,
                }}
              />
              {service.gallery.slice(0, 3).map((img, i) => (
                <div
                  key={i}
                  style={{
                    borderRadius: 'var(--radius-lg)',
                    background: `url(${img}) center/cover no-repeat`,
                  }}
                />
              ))}
            </div>

            {/* About */}
            <div
              style={{
                padding: '2rem',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: '1.5rem',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontSize: '1.5rem',
                  fontWeight: 400,
                  marginBottom: '0.75rem',
                  letterSpacing: '-0.005em',
                }}
              >
                About this service
              </div>
              <p
                style={{
                  fontSize: '1.0625rem',
                  lineHeight: 1.65,
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  marginBottom: '1.25rem',
                }}
              >
                {service.description}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {service.tags.map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </div>
            </div>

            {/* Reviews */}
            <div
              style={{
                padding: '2rem',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontSize: '1.5rem',
                    fontWeight: 400,
                    letterSpacing: '-0.005em',
                  }}
                >
                  Reviews
                </div>
                <RatingLine rating={service.rating} reviewCount={service.reviewCount} size={16} />
              </div>
              {reviews.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No reviews yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {reviews.map((r) => (
                    <div key={r.id} style={{ display: 'flex', gap: '1rem' }}>
                      <Avatar src={r.avatar} size={42} />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                            marginBottom: '0.375rem',
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{r.author}</span>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                            · {r.date}
                          </span>
                        </div>
                        <RatingLine rating={r.rating} size={13} />
                        <p
                          style={{
                            fontSize: '0.9375rem',
                            lineHeight: 1.6,
                            color: 'var(--color-text-secondary)',
                            margin: '0.5rem 0 0',
                          }}
                        >
                          {r.body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: sticky booking panel */}
          <aside
            style={{
              position: 'sticky',
              top: '100px',
              alignSelf: 'start',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
            className="svc-sticky"
          >
            {/* Packages */}
            <div
              style={{
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '0.375rem',
                  padding: '0.25rem',
                  background: 'var(--color-background-surface)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                }}
              >
                {service.packages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setActivePkg(p.id)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      flex: 1,
                      textAlign: 'center',
                      padding: '0.5rem 0.375rem',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      borderRadius: 'var(--radius-sm)',
                      background:
                        activePkg === p.id ? 'var(--color-background-card)' : 'transparent',
                      color: activePkg === p.id ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                      boxShadow: activePkg === p.id ? 'var(--shadow-subtle)' : 'none',
                      textTransform: 'capitalize',
                    }}
                  >
                    {p.tier}
                  </button>
                ))}
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <div
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontSize: '1.5rem',
                    fontWeight: 400,
                    marginBottom: '0.25rem',
                    letterSpacing: '-0.005em',
                  }}
                >
                  {pkg.name}
                </div>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-muted)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                  }}
                >
                  <Clock size={12} /> {pkg.duration}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.5rem',
                  marginBottom: '1.25rem',
                }}
              >
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  From
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontSize: '2rem',
                    fontWeight: 400,
                    color: 'var(--color-text-primary)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {pkg.priceFrom.toLocaleString()} ₼
                </span>
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.625rem',
                }}
              >
                {pkg.includes.map((item) => (
                  <li
                    key={item}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.625rem',
                      fontSize: '0.9375rem',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.45,
                    }}
                  >
                    <Check
                      size={16}
                      strokeWidth={2}
                      style={{ color: 'var(--color-accent-rosegold)', flexShrink: 0, marginTop: '2px' }}
                    />
                    {item}
                  </li>
                ))}
              </ul>

              <Button onClick={() => navigate('request')} size="lg" style={{ width: '100%' }}>
                Request this package
              </Button>
              <button
                onClick={() => navigate('messages')}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.75rem',
                  marginTop: '0.5rem',
                  border: '1px solid var(--color-border-medium)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                }}
              >
                <MessageCircle size={16} /> Message vendor
              </button>

              {/* Actions */}
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--color-border-light)',
                  justifyContent: 'center',
                  color: 'var(--color-text-muted)',
                  fontSize: '0.8125rem',
                }}
              >
                <button
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.375rem 0.75rem',
                  }}
                >
                  <Heart size={14} /> Save
                </button>
                <button
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.375rem 0.75rem',
                  }}
                >
                  <Share2 size={14} /> Share
                </button>
              </div>
            </div>

            {/* Vendor card */}
            <button
              onClick={() => navigate('vendor', { vendorId: vendor.id })}
              style={{
                all: 'unset',
                cursor: 'pointer',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <Avatar src={vendor.avatar} size={52} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.25rem',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{vendor.name}</span>
                  {vendor.verified && <VerifiedBadge />}
                </div>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-muted)',
                    lineHeight: 1.4,
                  }}
                >
                  Replies {vendor.responseTime} · {vendor.completedProjects} projects
                </div>
              </div>
            </button>
          </aside>
        </div>

        <style>{`
          @media (max-width: 1000px) {
            .svc-hero { grid-template-columns: 1fr !important; }
            .svc-sticky { position: static !important; }
            .svc-gallery { height: 340px !important; }
          }
          @media (max-width: 620px) {
            .svc-gallery { grid-template-columns: 1fr !important; grid-template-rows: repeat(3, 160px) !important; height: auto !important; }
            .svc-gallery > div:first-child { grid-row: auto !important; }
          }
        `}</style>
      </PageSection>
    </>
  );
}
